import type { EFMDomainCode } from 'src/features/domains/efmDomains';

export type { EFMDomainCode };

/**
 * Types for the actions feature
 */

export interface AssigneeInfo {
  user_id: number;
  name: string;
  email?: string | null;
}

// ============================================================================
// Group Types (unified with team-groups)
// ============================================================================

export type GroupType = 'standard' | 'safety' | 'pfi';

export const GROUP_TYPE_LABELS: Record<GroupType, string> = {
  standard: 'Standard Group',
  safety: 'Safety Group',
  pfi: 'PFI Group'
};

export const GROUP_TYPE_DESCRIPTIONS: Record<GroupType, string> = {
  standard: 'A general-purpose group for organising members, meetings and actions.',
  safety: 'A formally defined EFM group overseeing compliance, risk, and working practices in a specific domain.',
  pfi: 'A group for a PFI contract: obligations, evidence, and asset reviews.'
};

/** Legacy API field — all groups are organisation-visible and joinable. */
export type GroupVisibility = 'private' | 'discoverable';

/** Lifecycle states for a request to join a discoverable group. */
export type JoinRequestStatus = 'pending' | 'approved' | 'rejected' | 'withdrawn';

export type PopulateStage =
  | 'intro'
  | 'tor'
  | 'action_log'
  | 'members'
  | 'meeting'
  | 'documents'
  | 'training'
  | 'review_actions'
  | 'complete'
  | 'dismissed';

/** Wizard state — applies to every group type. */
export interface PopulateState {
  populate_stage: PopulateStage;
  completed_stages: PopulateStage[];
  populate_completed_at: string | null;
}

/** Safety-only attributes for safety groups (discipline + Terms of Reference). */
export interface SafetyGroup {
  domain: EFMDomainCode | null;
  terms_of_reference_id: string | null;
}

export interface Group {
  id: string;
  tenant: string;
  tenant_name: string;
  name: string;
  short_reference: string;
  description: string | null;
  type: GroupType;
  visibility: GroupVisibility;
  created_by_id: number | null;
  user_permission: number; // ResourcePermissionRole enum value
  created_at: string;
  updated_at: string;
  populate: PopulateState;
  safety_group: SafetyGroup | null;
  domain: EFMDomainCode | null;
}

export interface GroupCreate {
  name: string;
  description?: string | null;
  type?: GroupType;
  visibility?: GroupVisibility;
  domain?: EFMDomainCode | null;
  recurring_meeting?: {
    title: string;
    description?: string | null;
    default_location?: string | null;
    default_time?: string | null;
    repeat_type: 'daily' | 'weekly' | 'monthly' | 'yearly';
    repeat_interval?: number;
    repeat_days_of_week?: string | null;
    repeat_monthly_type?: 'day_of_month' | 'nth_weekday' | null;
    repeat_day_of_month?: number | null;
    repeat_week_ordinal?: number | null;
    repeat_weekday?: number | null;
    repeat_month?: number | null;
    repeat_start_date?: string | null;
    repeat_end_type?: 'never' | 'on_date';
    repeat_end_date?: string | null;
  } | null;
}

export interface GroupUpdate {
  name?: string;
  description?: string | null;
  visibility?: GroupVisibility;
  domain?: EFMDomainCode | null;
}

export interface GroupListResponse {
  groups: Group[];
  total: number;
}

// ============================================================================
// Domain catalogue (public.domains)
// ============================================================================

export interface CatalogueDomain {
  code: EFMDomainCode;
  name: string;
  description: string;
}

/**
 * Domains the caller's tenant must maintain a safety group for, driven
 * by the tenant's regulatory environment. Empty list = no domains
 * required (legacy tenant with no environment, or the platform hasn't
 * configured requirements for the environment) — the UI then degrades
 * to "all domains optional".
 */
export interface SafetyGroupRequirements {
  required_domains: EFMDomainCode[];
}

// ============================================================================
// Discoverable groups directory
// ============================================================================

/** Contact details for a group admin — surfaced to non-members so they know who to ask for access. */
export interface GroupAdminContact {
  user_id: number;
  email: string;
  firstname: string | null;
  surname: string | null;
}

export interface DiscoverableGroup {
  id: string;
  name: string;
  description: string | null;
  type: GroupType;
  domain: EFMDomainCode | null;
  domain_name: string | null;
  training_domain: EFMDomainCode | null;
  member_count: number;
  is_member: boolean;
  my_pending_request_id: string | null;
  admins: GroupAdminContact[];
}

/** Display name for an admin contact — falls back to email when no name is set. */
export function getAdminDisplayName(admin: GroupAdminContact): string {
  const name = [admin.firstname, admin.surname].filter(Boolean).join(' ').trim();
  return name || admin.email;
}

// ============================================================================
// Join requests
// ============================================================================

export interface JoinRequest {
  id: string;
  group_id: string;
  group_name: string | null;
  user_id: number;
  requester_email: string | null;
  requester_firstname: string | null;
  requester_surname: string | null;
  requester_organisation: string | null;
  status: JoinRequestStatus;
  message: string | null;
  requested_at: string;
  decided_at: string | null;
  decided_by_user_id: number | null;
}

export interface JoinRequestListResponse {
  requests: JoinRequest[];
  total: number;
}

// Group roles

export type GroupRoleType = 'admin' | 'contributor' | 'viewer';

export const GROUP_ROLE_LABELS: Record<GroupRoleType, string> = {
  admin: 'Admin',
  contributor: 'Contributor',
  viewer: 'Viewer'
};

export type AttendanceMatrixCellOutcome = 'present' | 'absent' | 'apologies' | 'invited' | 'no_record';

export interface AttendanceMatrixCell {
  outcome: AttendanceMatrixCellOutcome;
}

export interface AttendanceMatrixMeeting {
  meeting_id: string;
  scheduled_date: string;
  title: string;
}

export interface AttendanceMatrixRow {
  user_id: number;
  present_count: number;
  meetings_total: number;
  cells: AttendanceMatrixCell[];
}

export interface GroupMeetingAttendanceHeatmapResponse {
  meetings: AttendanceMatrixMeeting[];
  rows: AttendanceMatrixRow[];
}

export interface GroupMember {
  user_id: number;
  email: string;
  firstname: string | null;
  surname: string | null;
  organisation: string | null;
  group_role_type: GroupRoleType;
}

export interface GroupMembersResponse {
  members: GroupMember[];
  total: number;
}

export interface GroupResource {
  resource_type: string;
  resource_id: string;
  resource_name: string;
  shared_at: string;
  granter_name: string | null;
  resource_subtype?: string | null;
  relation?: string | null;
}

export interface GroupResourcesResponse {
  resources: GroupResource[];
  total: number;
}

// Stored status values (from backend)
export type ActionStoredStatus = 'open' | 'resolved' | 'archived';

export type ActionPriority = 'low' | 'medium' | 'high';

// Display status includes calculated 'overdue' (computed from due_date)
export type ActionDisplayStatus = ActionStoredStatus | 'overdue';

export interface Action {
  id: string;
  reference_id: string;
  tenant: string;
  short_reference: string;
  /** Owning group. Null for actions that are not in a group. */
  group_id?: string | null;
  group_name?: string | null;
  description: string;
  due_date: string | null;
  priority?: ActionPriority | null;
  status: ActionStoredStatus;
  creator_id: number | null;
  assignees: AssigneeInfo[];
  created_at: string;
  updated_at: string;
  origin_type?: string | null;
  origin_reference_id?: string | null;
  cost?: number | null;
  revenue_cost?: number | null;
  capital_cost?: number | null;
  links?: ActionResourceLinkInfo[];
  user_permission?: number | null;
  // True only for PAM costed actions the current user may request an AI cost
  // estimate for (action-edit rights on the linked question). Populated by the
  // single-action fetch; absent/false in list responses.
  can_estimate_cost?: boolean;
  tags?: ResourceTag[];
}

export type TagGroup = 'document_type' | 'domain' | 'site' | 'national_returns';
export type TagOrigin = 'llm' | 'human' | 'inherited';
export type TagStatus = 'suggested' | 'approved' | 'rejected';

export interface ResourceTag {
  id: string;
  tag_id: string;
  group: TagGroup;
  slug: string;
  label: string;
  origin: TagOrigin;
  status: TagStatus;
  source_resource_id?: string | null;
  confidence?: number | null;
  sources?: { chunk_id: string }[];
}

export interface TagCatalogGroup {
  slug: TagGroup;
  label: string;
  cardinality: 'single' | 'multiple';
  allow_user_defined: boolean;
  tags: { id: string; group: TagGroup; slug: string; label: string }[];
}

export interface ActionResourceLinkInfo {
  resource_id: string;
  resource_type: string;
  title: string;
}

export interface ActionCreate {
  description: string;
  due_date?: string | null;
  priority?: ActionPriority | null;
  assignee_emails?: string[] | null;
  group_id?: string | null;
  origin_type?: string | null;
  origin_reference_id?: string | null;
  meeting_id?: string | null;
  /** Optional total estimated cost, only used for PAM costed actions. */
  cost?: number | null;
  /** Optional revenue portion of the estimated cost (PAM costed actions). */
  revenue_cost?: number | null;
  /** Optional capital portion of the estimated cost (PAM costed actions). */
  capital_cost?: number | null;
  tag_ids?: string[] | null;
}

export interface ActionUpdate {
  description?: string;
  due_date?: string | null;
  priority?: ActionPriority | null;
  /** Optional total estimated cost, used by PAM costed actions. */
  cost?: number | null;
  /** Optional revenue portion of the estimated cost (PAM costed actions). */
  revenue_cost?: number | null;
  /** Optional capital portion of the estimated cost (PAM costed actions). */
  capital_cost?: number | null;
  /**
   * When the cost being saved came from an accepted AI estimate, its
   * justification. The backend records it as a progress note for auditability;
   * it is not stored as an action field.
   */
  ai_cost_rationale?: string | null;
  assignee_emails?: string[] | null;
  meeting_id?: string | null;
  tag_ids?: string[] | null;
}

export interface ActionListResponse {
  actions: Action[];
  total: number;
}

export interface OrganizationUser {
  user_id: number;
  email: string;
  firstname?: string | null;
  surname?: string | null;
}

/**
 * Get display text for multiple assignees.
 * Returns "Unassigned", a single name, or "Alice, Bob" / "Alice +2 more".
 */
export function getAssigneesDisplayName(assignees: AssigneeInfo[], maxNames = 2): string {
  if (!assignees || assignees.length === 0) return 'Unassigned';
  if (assignees.length === 1) return assignees[0].name;
  if (assignees.length <= maxNames) {
    return assignees.map((a) => a.name).join(', ');
  }
  const shown = assignees
    .slice(0, maxNames)
    .map((a) => a.name)
    .join(', ');
  return `${shown} +${assignees.length - maxNames} more`;
}

/**
 * Generate all contiguous word subsequences from a text, longest first.
 * Used for fuzzy matching suggested assignee names against user lists.
 */
function generateWordSubsequences(text: string): string[] {
  const words = text
    .toLowerCase()
    .split(/\s+/)
    .filter((w) => w.trim().length > 0);
  if (words.length === 0) return [];
  const subsequences: string[] = [];
  for (let start = 0; start < words.length; start++) {
    for (let end = start; end < words.length; end++) {
      subsequences.push(words.slice(start, end + 1).join(' '));
    }
  }
  return subsequences.sort((a, b) => b.length - a.length);
}

/**
 * Match a single suggested assignee name against a list of users.
 * Returns the matching user's email, or null if no match found.
 */
function matchSingleAssigneeName(name: string, users: OrganizationUser[]): string | null {
  const trimmed = name.trim();
  if (!trimmed) return null;

  const subsequences = generateWordSubsequences(trimmed);
  if (subsequences.length === 0) return null;

  const userNameMap = new Map<string, string>();
  for (const user of users) {
    if (!user.firstname && !user.surname) continue;
    const fullName = `${user.firstname || ''} ${user.surname || ''}`.trim().toLowerCase();
    if (fullName) userNameMap.set(fullName, user.email);
  }

  for (const subsequence of subsequences) {
    const matchedEmail = userNameMap.get(subsequence);
    if (matchedEmail !== undefined) return matchedEmail;
  }
  return null;
}

/** Split and dedupe suggested assignee strings (comma/semicolon lists allowed). */
export function parseSuggestedAssigneeNames(
  suggestedAssignees: Array<string | null | undefined> | null | undefined
): string[] {
  if (!suggestedAssignees?.length) return [];
  const names: string[] = [];
  const seen = new Set<string>();
  for (const suggested of suggestedAssignees) {
    for (const name of (suggested ?? '')
      .split(/[,;]/)
      .map((part) => part.trim())
      .filter(Boolean)) {
      const key = name.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      names.push(name);
    }
  }
  return names;
}

/**
 * Resolve a suggested assignee string into matching user emails.
 *
 * Handles comma or semicolon separated names (e.g. "Mark Johnson, Local User")
 * by splitting and matching each part independently.
 *
 * @returns Array of matched emails (may be empty if no matches found)
 */
export function findMatchingAssigneeEmails(suggestedAssignee: string | null, users: OrganizationUser[]): string[] {
  if (!suggestedAssignee || users.length === 0) return [];

  // Split on commas or semicolons to handle multiple names
  const parts = suggestedAssignee
    .split(/[,;]/)
    .map((p) => p.trim())
    .filter(Boolean);

  if (parts.length === 0) return [];

  const emails: string[] = [];
  const seen = new Set<string>();

  for (const part of parts) {
    const email = matchSingleAssigneeName(part, users);
    if (email && !seen.has(email.toLowerCase())) {
      seen.add(email.toLowerCase());
      emails.push(email);
    }
  }

  return emails;
}
